import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
// Side-effect import: the widget is plain browser JS that registers
// window.WekalpConsent. Importing the source rather than fetching /widget.js
// means the preview runs the exact bundle that ships to customers, and keeps
// working in dev where nothing serves /widget.js.
import '@wekalp/widget/src/widget.js';

/**
 * The form as a person will see it.
 *
 * This mounts the real consent widget against the real widget payload — the
 * preview endpoint returns the same shape as /v1/widget/config. Nothing here
 * re-implements field rendering, so the preview cannot drift from what ships:
 * if the widget changes, this changes with it.
 *
 * It submits nothing. Validation still runs, so an operator can watch their own
 * rules and messages behave, but no consent is recorded and no principal is
 * created.
 */

declare global {
  interface Window {
    WekalpConsent?: {
      mount(options: Record<string, unknown>): Promise<unknown>;
      instances: unknown[];
    };
  }
}

export function FormPreview({
  formId, version, lang, onLangChange,
}: {
  formId: string;
  version: number;
  lang: string;
  onLangChange?: (lang: string) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const target = host.current;
    if (!target || !window.WekalpConsent) return;

    setLoading(true);
    setError(null);

    window.WekalpConsent.mount({
      target,
      preview: true,
      lang,
      // The widget asks for its config; the console answers from the preview
      // endpoint, which can render an unpublished draft.
      loadConfig: (wanted: string) => {
        onLangChange?.(wanted);
        return api.get(
          `/v1/forms/${formId}/preview?version=${version}&lang=${encodeURIComponent(wanted)}`,
        );
      },
    })
      .then(() => { if (!cancelled) setLoading(false); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      // The widget owns a shadow root on this node; clearing it lets a
      // re-mount start clean rather than stacking a second copy inside.
      if (target.shadowRoot) target.shadowRoot.innerHTML = '';
    };
  }, [formId, version, lang]);

  return (
    <>
      {loading && <div className="small muted">Rendering the form…</div>}
      {error && <div className="note bad">{error}</div>}
      <div className="fb-preview">
        <div ref={host} />
      </div>
      <div className="small muted" style={{ marginTop: 10 }}>
        Preview of the saved draft. Validation runs; nothing is recorded.
      </div>
    </>
  );
}
